import React, { useEffect, useState } from 'react'
import {
  List,
  Modal as AntdModal,
  Modal,
  notification,
  Spin,
  Typography
} from 'antd'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { Empty } from 'src/components/atoms'
import { Id, RequestTicket } from 'src/types'
import { ResponseDataRequestsItem } from 'src/core/store/ducks/requests/types'
import * as RequestService from 'src/services/api/requests'
import * as TicketsService from 'src/services/api/tickets'
import sanitizeToPost from './sanitize-to-post'

export type TicketChecked = RequestTicket & { checked: boolean }
type ListRequestRenderProps = {
  request: ResponseDataRequestsItem
  select: React.Dispatch<ResponseDataRequestsItem>
}

type ModalLinkRequestCreatedProps = {
  modalClose: () => void
  organization_id: Id
  ticketsList: TicketChecked[]
  updateRequestList: () => void
  user_id: Id
  visible: boolean
}

type ModalRenderProps = {
  isLoading: boolean
  listRequest: ResponseDataRequestsItem[]
  modalClose: () => void
  organization_id: Id
  ticketsList: TicketChecked[]
  updateRequestList: () => void
  user_id: Id
}

function ListRequestRender(props: ListRequestRenderProps) {
  return (
    <List.Item
      onClick={() => {
        props.select(props.request)
      }}
    >
      <Typography.Title level={4}>
        {props.request.request_number}
      </Typography.Title>
      <Typography.Text>{props.request.title}</Typography.Text>
      <Typography.Text>
        {moment.unix(props.request.requested_on).utc().format('DD/MM/YYYY')}
      </Typography.Text>
    </List.Item>
  )
}

function ModalRender(props: ModalRenderProps) {
  const [selectedRequest, setSelectedRequest] =
    useState<ResponseDataRequestsItem>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()

  if (props.isLoading) {
    return <Spin />
  }

  return (
    <>
      <List
        dataSource={props.listRequest}
        header={<div>{t('web.single-ticket.select-old-request')}:</div>}
        locale={{ emptyText: <Empty /> }}
        renderItem={item => (
          <ListRequestRender request={item} select={setSelectedRequest} />
        )}
      />
      <Modal
        cancelButtonProps={{ loading: isLoading }}
        closable
        destroyOnClose
        okButtonProps={{ loading: isLoading }}
        onCancel={() => {
          setSelectedRequest(null)
        }}
        onOk={() => {
          setIsLoading(true)
          const sanitized = sanitizeToPost(
            selectedRequest.request_id,
            props.ticketsList
          )

          TicketsService.saveTickets({
            organization_id: props.organization_id,
            request_id: selectedRequest.request_id,
            tickets: sanitized
          })
            .then(response => {
              if (response.status !== 200) {
                notification.error({
                  duration: 8,
                  message:
                    typeof response.data?.reasons === 'string'
                      ? response.data?.reasons
                      : t('common.response.status-500'),
                  placement: 'bottomRight'
                })
              }
              props.updateRequestList()
              setSelectedRequest(null)
              props.modalClose()
            })
            .catch(response => {
              notification.error({
                duration: 8,
                message:
                  typeof response.data?.reasons === 'string'
                    ? response.data?.reasons
                    : t('common.response.status-500'),
                placement: 'bottomRight'
              })
            })
            .finally(() => {
              setIsLoading(false)
            })
        }}
        title={t('common.actions.delete.title')}
        visible={Boolean(selectedRequest)}
      >
        <div>
          {`${t('web.single-ticket.attach-old-request')} #${
            selectedRequest?.request_number
          }`}
          <br />
          {t('common.actions.delete.no-undo')}
        </div>
      </Modal>
    </>
  )
}

export default function ModalLinkRequestCreated(
  props: ModalLinkRequestCreatedProps
) {
  const [isLoading, setIsLoading] = useState(false)
  const [listRequest, setListRequest] = useState<ResponseDataRequestsItem[]>([])
  const { t } = useTranslation()

  useEffect(() => {
    if (!props.visible) return

    let isSubscribed = true
    setIsLoading(true)

    RequestService.GetRequestsInUnderway({
      organization_id: props.organization_id
    })
      .then(response => {
        if (!isSubscribed) return
        if (response.status === 200) {
          setListRequest(response.data.requests)
          return
        }

        //@ts-ignore
        const reasons = response.data.reasons
        notification.error({
          duration: 8,
          message: typeof reasons === 'string' ? reasons : 'Erro desconhecido',
          placement: 'bottomRight'
        })
      })
      .catch(response => {
        if (!isSubscribed) return
        const reasons = response.data.reasons
        notification.error({
          duration: 8,
          message: typeof reasons === 'string' ? reasons : 'Erro desconhecido',
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        if (!isSubscribed) return
        setIsLoading(false)
      })
  }, [props.visible, props.organization_id])

  return (
    <AntdModal
      destroyOnClose
      okButtonProps={{ hidden: true }}
      onCancel={() => {
        props.modalClose()
      }}
      title={t('web.audits.title')}
      visible={props.visible}
    >
      <ModalRender
        isLoading={isLoading}
        listRequest={listRequest}
        modalClose={props.modalClose}
        organization_id={props.organization_id}
        ticketsList={props.ticketsList}
        updateRequestList={props.updateRequestList}
        user_id={props.user_id}
      />
    </AntdModal>
  )
}
